/**
 * OneFleet platform: alerts and notifications (WP-A9) on `/int/v1/platform/*` — the alerts the rules raised (open by
 * default, severity first) with acknowledge / resolve, the summary strip, and the signed-in person's in-app
 * notifications (their own and their roles') with mark-read. Every row is a rule's finding (`source`), never free text.
 * D-59 adds the agents' daily schedules: the tenant's switches per job and the worker's last runs; D-147 the
 * comments on a record — a note carries no permission of its own, so the panel is shown wherever the record is;
 * D-148 the documents a fleet prints (waybill, job card, hand-over note) and the tenant's own wording of them.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { z } from "zod";
import { fleetHeaders, fleetUrl, onefleet } from "./client";
import { fleetKeys } from "./queries";

const envelope = async <T>(promise: Promise<{ data: T }>): Promise<T> =>
  (await promise).data;
const PL = "/int/v1/platform";

export const AlertSchema = z.object({
  id: z.string(),
  kind: z.string(),
  severity: z.string(),
  status: z.string(),
  subject_type: z.string().nullable(),
  subject_id: z.string().nullable(),
  subject_label: z.string().nullable(),
  title: z.string(),
  message: z.string(),
  fingerprint: z.string(),
  evidence: z.record(z.string(), z.unknown()).default({}),
  source: z.string(),
  count: z.number(),
  raised_at: z.string().nullable(),
  last_seen_at: z.string().nullable(),
  acknowledged_at: z.string().nullable(),
  acknowledged_by: z.string().nullable(),
  resolved_at: z.string().nullable(),
  resolved_by: z.string().nullable(),
  resolution: z.string().nullable(),
  version: z.number(),
});
export type Alert = z.infer<typeof AlertSchema>;

export const AlertSummarySchema = z.object({
  open: z.record(z.string(), z.number()).default({}),
  by_severity: z.record(z.string(), z.number()).default({}),
  total: z.number().default(0),
  last_raised_at: z.string().nullable().optional(),
});
export type AlertSummary = z.infer<typeof AlertSummarySchema>;

export const NotificationSchema = z.object({
  id: z.string(),
  channel: z.string(),
  template: z.string(),
  params: z.record(z.string(), z.unknown()).default({}),
  language: z.string(),
  user_id: z.string().nullable(),
  role: z.string().nullable(),
  title: z.string(),
  body: z.string(),
  link: z.string().nullable(),
  severity: z.string(),
  alert_id: z.string().nullable(),
  status: z.string(),
  sent_at: z.string().nullable(),
  read_at: z.string().nullable(),
  source: z.string(),
  created_at: z.string().nullable(),
});
export type Notification = z.infer<typeof NotificationSchema>;

const Rows = <T extends z.ZodTypeAny>(row: T) =>
  z.object({
    data: z.array(row),
    meta: z.object({ total: z.number() }).passthrough(),
  });

export const AiScheduleJobSchema = z.object({
  at: z.string(),
  agent: z.string(),
  enabled: z.boolean(),
  propose: z.boolean(),
  unassigned_threshold: z.number().optional(),
  notify_roles: z.array(z.string()),
  link: z.string(),
});
export const AiSchedulesSchema = z.object({
  language: z.string(),
  jobs: z.record(z.string(), AiScheduleJobSchema),
  last: z.record(z.string(), z.record(z.string(), z.unknown())),
});
export type AiSchedules = z.infer<typeof AiSchedulesSchema>;
export type AiSchedulesPatch = {
  language?: string;
  jobs?: Record<
    string,
    { enabled?: boolean; propose?: boolean; unassigned_threshold?: number }
  >;
};

export const platformKeys = {
  all: [...fleetKeys.all, "platform"] as const,
  aiSchedules: (company?: string) =>
    [...fleetKeys.all, "platform", "ai-schedules", company ?? ""] as const,
  alerts: (status: string, company?: string) =>
    [...fleetKeys.all, "platform", "alerts", status, company ?? ""] as const,
  alertsSummary: (company?: string) =>
    [...fleetKeys.all, "platform", "alerts", "summary", company ?? ""] as const,
  notifications: (company?: string) =>
    [...fleetKeys.all, "platform", "notifications", company ?? ""] as const,
  complianceRules: (company?: string) =>
    [
      ...fleetKeys.all,
      "platform",
      "compliance",
      "rules",
      company ?? "",
    ] as const,
  alertRules: (company?: string) =>
    [...fleetKeys.all, "platform", "alert-rules", company ?? ""] as const,
  comments: (subjectType: string, subject: string, company?: string) =>
    [
      ...fleetKeys.all,
      "platform",
      "comments",
      subjectType,
      subject,
      company ?? "",
    ] as const,
  documents: (company?: string) =>
    [...fleetKeys.all, "platform", "documents", company ?? ""] as const,
};

/** The Compliance Monitor's rule catalogue with the tenant's thresholds and switches (OneFleet D-77). */
export const ComplianceKindSchema = z.object({
  kind: z.string(),
  enabled: z.boolean(),
  severity: z.string().nullable(),
  notify: z.boolean(),
});
export const ComplianceThresholdSchema = z.object({
  key: z.string(),
  value: z.number(),
  default: z.number(),
  min: z.number(),
  max: z.number(),
  unit: z.string(),
  source: z.string(),
});
export const ComplianceRuleSchema = z.object({
  rule: z.string(),
  owner: z.string(),
  kinds: z.array(ComplianceKindSchema),
  thresholds: z.array(ComplianceThresholdSchema),
});
export const ComplianceRulesSchema = z.object({
  rules: z.array(ComplianceRuleSchema),
  config: z
    .object({
      thresholds: z.record(z.string(), z.number()),
      kinds: z.record(
        z.string(),
        z.object({
          enabled: z.boolean(),
          severity: z.string().nullable(),
          notify: z.boolean(),
        }),
      ),
      source: z.record(z.string(), z.string()),
    })
    .passthrough(),
});
export type ComplianceRule = z.infer<typeof ComplianceRuleSchema>;
export type ComplianceRules = z.infer<typeof ComplianceRulesSchema>;
export type ComplianceThresholdsPatch = {
  thresholds?: Record<string, number | null>;
  kinds?: Record<
    string,
    { enabled?: boolean; severity?: string | null; notify?: boolean }
  >;
};

/** The tenant's custom alert rules on events (D-96): the rules, the ops, the once-per choices, the event types seen. */
export const AlertRuleConditionSchema = z.object({
  path: z.string(),
  op: z.string(),
  value: z.unknown().optional(),
});
export const AlertRuleSchema = z.object({
  id: z.string().optional(),
  name: z.string(),
  event: z.string(),
  when: z.array(AlertRuleConditionSchema).default([]),
  severity: z.string(),
  title: z.string(),
  message: z.string().nullable().optional(),
  enabled: z.boolean().default(true),
  notify_role: z.string().nullable().optional(),
  once_per: z.string().default("subject"),
});
export const AlertRulesSchema = z.object({
  rules: z.array(AlertRuleSchema),
  ops: z.array(z.string()),
  once_per: z.array(z.string()),
  severities: z.array(z.string()),
  events: z.array(
    z.object({ type: z.string(), count: z.number() }).passthrough(),
  ),
});
export type AlertRuleCondition = z.infer<typeof AlertRuleConditionSchema>;
export type AlertRule = z.infer<typeof AlertRuleSchema>;
export type AlertRules = z.infer<typeof AlertRulesSchema>;

export function useAlertRules(company?: string, enabled = true) {
  return useQuery({
    queryKey: platformKeys.alertRules(company),
    queryFn: async () =>
      AlertRulesSchema.parse(
        await envelope(onefleet.get(`${PL}/alert-rules`, { company })),
      ),
    enabled,
    staleTime: 30_000,
  });
}

export function useAlertRuleActions(company?: string) {
  const qc = useQueryClient();
  const setRules = useMutation({
    mutationFn: (rules: AlertRule[]) =>
      envelope(
        onefleet.put<{ data: { rules: unknown } }>(
          `${PL}/alert-rules`,
          { rules },
          { company },
        ),
      ),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: platformKeys.alertRules(company) }),
  });
  const tryRule = useMutation({
    mutationFn: (body: {
      rule: AlertRule;
      event_type: string;
      payload: Record<string, unknown>;
    }) =>
      envelope(
        onefleet.post<{
          data: {
            matches: boolean;
            title: string | null;
            message: string | null;
          };
        }>(`${PL}/alert-rules/try`, body, { company }),
      ),
  });
  return { setRules, tryRule };
}

export function useComplianceRules(company?: string, enabled = true) {
  return useQuery({
    queryKey: platformKeys.complianceRules(company),
    queryFn: async () =>
      ComplianceRulesSchema.parse(
        await envelope(onefleet.get(`${PL}/compliance/rules`, { company })),
      ),
    enabled,
    staleTime: 30_000,
  });
}

export function useAlerts(status = "open", company?: string, enabled = true) {
  return useQuery({
    queryKey: platformKeys.alerts(status, company),
    queryFn: async () =>
      Rows(AlertSchema).parse(
        await onefleet.get(`${PL}/alerts`, {
          company,
          query: { status, limit: 100 },
        }),
      ),
    enabled,
    staleTime: 15_000,
  });
}

/** The alerts centre's list (WP-U15): status (`active` = open + acknowledged), severity and kind filters. */
export function useAlertsFiltered({
  status = "active",
  severity,
  kind,
  company,
  enabled = true,
}: {
  status?: string;
  severity?: string;
  kind?: string;
  company?: string;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: [
      ...platformKeys.alerts(status, company),
      severity ?? "",
      kind ?? "",
    ] as const,
    queryFn: async () =>
      Rows(AlertSchema).parse(
        await onefleet.get(`${PL}/alerts`, {
          company,
          query: { status, severity, kind, limit: 200 },
        }),
      ),
    enabled,
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  });
}

export function useAlertsSummary(company?: string, enabled = true) {
  return useQuery({
    queryKey: platformKeys.alertsSummary(company),
    queryFn: async () =>
      AlertSummarySchema.parse(
        await envelope(onefleet.get(`${PL}/alerts/summary`, { company })),
      ),
    enabled,
    staleTime: 15_000,
  });
}

/** Mine and my roles' in-app notifications; `refetchMs` keeps the shell's bell current where the socket is closed (D-80). */
export function useMyNotifications(
  company?: string,
  enabled = true,
  refetchMs?: number,
) {
  return useQuery({
    queryKey: platformKeys.notifications(company),
    queryFn: async () =>
      Rows(NotificationSchema)
        .extend({
          meta: z
            .object({ total: z.number(), unread: z.number() })
            .passthrough(),
        })
        .parse(
          await onefleet.get(`${PL}/notifications`, {
            company,
            query: { limit: 50 },
          }),
        ),
    enabled,
    staleTime: 15_000,
    refetchInterval: refetchMs,
  });
}

/** The tenant's patch over the current doc, the way the core merges it (a job's keys merge; `last` untouched). */
export function applySchedulesPatch(
  doc: AiSchedules,
  patch: AiSchedulesPatch,
): AiSchedules {
  const jobs = { ...doc.jobs };
  for (const [k, row] of Object.entries(patch.jobs ?? {}))
    if (jobs[k]) jobs[k] = { ...jobs[k], ...row };
  return { ...doc, language: patch.language ?? doc.language, jobs };
}

export function useAiSchedules(company?: string, enabled = true) {
  return useQuery({
    queryKey: platformKeys.aiSchedules(company),
    queryFn: async () =>
      AiSchedulesSchema.parse(
        await envelope(onefleet.get(`${PL}/ai-schedules`, { company })),
      ),
    enabled,
    staleTime: 15_000,
  });
}

/** The tenant's switches (`settings:write`): a job on / off, its `propose`, the route advice's unplanned threshold, the language. */
export function useAiSchedulesActions(company?: string) {
  const qc = useQueryClient();
  const key = platformKeys.aiSchedules(company);
  const save = useMutation({
    mutationFn: (patch: AiSchedulesPatch) =>
      envelope(
        onefleet.put<{ data: AiSchedules }>(`${PL}/ai-schedules`, patch, {
          company,
        }),
      ),
    // the switch flips at once (a controlled checkbox waiting for the round trip reads as stuck); the server's doc replaces it
    onMutate: async (patch) => {
      await qc.cancelQueries({ queryKey: key });
      const before = qc.getQueryData<AiSchedules>(key);
      if (before)
        qc.setQueryData<AiSchedules>(key, applySchedulesPatch(before, patch));
      return { before };
    },
    onError: (_e, _p, ctx) => {
      if (ctx?.before) qc.setQueryData(key, ctx.before);
    },
    onSuccess: (data) => qc.setQueryData(key, data),
  });
  return { save };
}

export function usePlatformActions(company?: string) {
  const qc = useQueryClient();
  const invalidate = () =>
    void qc.invalidateQueries({ queryKey: platformKeys.all });
  const post = <T>(path: string, body: unknown = {}) =>
    envelope(onefleet.post<{ data: T }>(`${PL}${path}`, body, { company }));
  const acknowledge = useMutation({
    mutationFn: (id: string) => post<Alert>(`/alerts/${id}/acknowledge`),
    onSuccess: invalidate,
  });
  const resolve = useMutation({
    mutationFn: ({
      id,
      resolution,
    }: {
      id: string;
      resolution?: string | null;
    }) =>
      post<Alert>(`/alerts/${id}/resolve`, { resolution: resolution ?? null }),
    onSuccess: invalidate,
  });
  const markRead = useMutation({
    mutationFn: (id: string) => post<Notification>(`/notifications/${id}/read`),
    onSuccess: invalidate,
  });
  const setComplianceRules = useMutation({
    mutationFn: (patch: ComplianceThresholdsPatch) =>
      envelope(
        onefleet.put<{ data: ComplianceRules }>(
          `${PL}/compliance/thresholds`,
          patch,
          { company },
        ),
      ).then((d) => ComplianceRulesSchema.parse(d)),
    onSuccess: invalidate,
  });
  return { acknowledge, resolve, markRead, setComplianceRules, invalidate };
}

// --- comments (WP-B16, D-147) ----------------------------------------------------------------------------------
/**
 * A note on a record: *the second gate is locked on Sundays*, *this truck pulls left under load*. The API keeps
 * what an edit replaced (`history`), so the panel can say a note was changed without losing what it said.
 */
export const CommentSchema = z.object({
  id: z.string(),
  subject_type: z.string(),
  subject_id: z.string(),
  body: z.string(),
  author_id: z.string().nullable(),
  author_name: z.string().nullable(),
  pinned: z.boolean(),
  edited_at: z.string().nullable(),
  history: z
    .array(z.object({ body: z.string(), replaced_at: z.string().optional() }))
    .default([]),
  /** `request` from the customer, `reply` addressed to them — set, this note is in their portal (D-150) */
  meta: z.object({ portal: z.string().optional() }).passthrough().default({}),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable(),
  version: z.number(),
});
export type Comment = z.infer<typeof CommentSchema>;

const CommentsSchema = z.object({
  data: z.array(CommentSchema),
  meta: z.object({ total: z.number(), pinned: z.number() }),
});

/** The notes on one record, pinned first then the newest — the order the API answers in, kept as it comes. */
export function useComments(
  subjectType: string,
  subject: string | undefined,
  company?: string,
  enabled = true,
) {
  return useQuery({
    queryKey: platformKeys.comments(subjectType, subject ?? "", company),
    queryFn: async () =>
      CommentsSchema.parse(
        await onefleet.get(`${PL}/comments`, {
          company,
          query: { subject_type: subjectType, subject: subject ?? "" },
        }),
      ),
    enabled: enabled && !!subject,
    staleTime: 10_000,
  });
}

export function useCommentActions(
  subjectType: string,
  subject: string | undefined,
  company?: string,
) {
  const qc = useQueryClient();
  const invalidate = () =>
    void qc.invalidateQueries({
      queryKey: platformKeys.comments(subjectType, subject ?? "", company),
    });
  const write = useMutation({
    mutationFn: ({
      body,
      toCustomer,
    }: {
      body: string;
      toCustomer?: boolean;
    }) =>
      envelope(
        onefleet.post<{ data: Comment }>(
          `${PL}/comments`,
          {
            subject_type: subjectType,
            subject: subject ?? "",
            body,
            to_customer: !!toCustomer,
          },
          { company },
        ),
      ),
    onSuccess: invalidate,
  });
  const edit = useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) =>
      envelope(
        onefleet.patch<{ data: Comment }>(
          `${PL}/comments/${id}`,
          { body },
          { company },
        ),
      ),
    onSuccess: invalidate,
  });
  const pin = useMutation({
    mutationFn: ({ id, pinned }: { id: string; pinned: boolean }) =>
      envelope(
        onefleet.patch<{ data: Comment }>(
          `${PL}/comments/${id}`,
          { pinned },
          { company },
        ),
      ),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) =>
      onefleet.delete(`${PL}/comments/${id}`, { company }),
    onSuccess: invalidate,
  });
  return { write, edit, pin, remove, invalidate };
}

// --- the documents a fleet prints (WP-B16, D-148) ---------------------------------------------------------------
/**
 * A printable document: the waybill a driver carries, the job card on the workshop bench, the hand-over note for
 * a truck. `shipped` is what the deployment ships in each language, `tenant` the words this company wrote; a body
 * may name `fields` and `blocks` and nothing else — the API refuses the rest when it is saved.
 */
export const DocumentTemplateSchema = z.object({
  id: z.string(),
  kind: z.string(),
  language: z.string(),
  title: z.string().nullable(),
  body: z.string(),
  enabled: z.boolean(),
  version: z.number(),
});
export type DocumentTemplate = z.infer<typeof DocumentTemplateSchema>;

export const DocumentKindSchema = z.object({
  key: z.string(),
  subject_type: z.string(),
  title: z.record(z.string(), z.string()),
  fields: z.array(z.string()),
  blocks: z.array(z.string()),
  shipped: z.record(z.string(), z.string()),
  tenant: z.record(z.string(), DocumentTemplateSchema.nullable()),
});
export type DocumentKind = z.infer<typeof DocumentKindSchema>;

export const DocumentCatalogueSchema = z.object({
  languages: z.array(z.string()),
  kinds: z.array(DocumentKindSchema),
});
export type DocumentCatalogue = z.infer<typeof DocumentCatalogueSchema>;

export const RenderedDocumentSchema = z.object({
  kind: z.string(),
  language: z.string(),
  title: z.string(),
  markdown: z.string(),
  html: z.string(),
});
export type RenderedDocument = z.infer<typeof RenderedDocumentSchema>;

export function useDocumentCatalogue(company?: string, enabled = true) {
  return useQuery({
    queryKey: platformKeys.documents(company),
    queryFn: async () =>
      DocumentCatalogueSchema.parse(
        await onefleet.get(`${PL}/documents`, { company }),
      ),
    enabled,
    staleTime: 60_000,
  });
}

export function useDocumentActions(company?: string) {
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: ({
      kind,
      language,
      body,
      title,
    }: {
      kind: string;
      language: string;
      body: string | null;
      title?: string | null;
    }) =>
      envelope(
        onefleet.put<{ data: DocumentTemplate | null }>(
          `${PL}/documents/${kind}`,
          { language, body, title: title ?? null },
          { company },
        ),
      ),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: platformKeys.documents(company) }),
  });
  return { save };
}

/** One rendered document — asked for on demand (a preview, or the moment somebody clicks *Print*). */
export async function fetchDocument(
  kind: string,
  ref: string,
  language: string,
  company?: string,
): Promise<RenderedDocument> {
  return RenderedDocumentSchema.parse(
    await envelope(
      onefleet.get<{ data: unknown }>(`${PL}/documents/${kind}/${ref}`, {
        company,
        query: { language },
      }),
    ),
  );
}

/**
 * Print a document. The page is behind the session's headers, so a plain link cannot fetch it: the HTML is read
 * here and handed to a new window, which prints it. The object URL is released once that window has it.
 */
export async function printDocument(
  kind: string,
  ref: string,
  language: string,
  company?: string,
): Promise<void> {
  const doc = await fetchDocument(kind, ref, language, company);
  const url = URL.createObjectURL(
    new Blob([doc.html], { type: "text/html;charset=utf-8" }),
  );
  const win = window.open(url, "_blank", "noopener");
  if (!win) {
    URL.revokeObjectURL(url);
    throw new Error("the browser blocked the document window");
  }
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// --- files (D-137) --------------------------------------------------------------------------------------------
/**
 * A platform file shown in the console — a proof of delivery photo, a signature, a fuel receipt.
 *
 * The bytes are behind the session's headers, so an `<img src>` cannot fetch them directly: the file is read
 * once with `fleetFetch`'s headers and turned into an object URL, which the browser may render. The URL is
 * revoked when the component that asked for it goes away, so a long console session does not leak blobs.
 */
export function useFileObjectUrl(
  url: string | null | undefined,
  company?: string,
): { src: string | null; error: string | null } {
  const [state, setState] = useState<{
    src: string | null;
    error: string | null;
  }>({ src: null, error: null });
  useEffect(() => {
    if (!url) {
      setState({ src: null, error: null });
      return;
    }
    let objectUrl: string | null = null;
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(fleetUrl(url), {
          headers: fleetHeaders(company),
        });
        if (!res.ok) throw new Error(`OneFleet ${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setState({ src: objectUrl, error: null });
      } catch (e) {
        if (!cancelled)
          setState({
            src: null,
            error: e instanceof Error ? e.message : String(e),
          });
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url, company]);
  return state;
}
